import {Sortable} from "./sortable";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {handleActivationKey} from "../../../common/utils/events";
import {ClickTracking} from "../../../tracking/clickTracking";
import {resolve} from "../../../container";

export const COLLAPSED_ROW_CLASS = "collapsed-row";
export const COLLAPSED_ROW_HEADER_CLASS = "collapsed-row-header";

export class EopTable extends HTMLElement {
    private collapsibleRows: CollapsibleRows[];

    public connectedCallback(): void {
        const rawSortableColumns: Nullable<string> = this.getAttribute("sortable-columns");
        if (rawSortableColumns !== null) {
            const sortableColumns = rawSortableColumns.replaceAll(/[\[\]\s]/g, "").split(",").map(it => Number.parseInt(it));
            const sortable = new Sortable(this);
            sortable.init(sortableColumns);
        }

        this.initCollapsibleTable();
    }

    private initCollapsibleTable(): void {
        let headerRow: Maybe<Element> = undefined;
        let rows: Element[] = [];
        this.collapsibleRows = [];

        for (const row of this.querySelectorAll("tbody ." + COLLAPSED_ROW_CLASS)) {
            if (row.previousElementSibling === null) {
                const theadRow = this.querySelector("thead tr");
                if (theadRow === null) {
                    headerRow = row;
                    continue;
                }
                headerRow = theadRow;
            } else if (!row.previousElementSibling.classList.contains(COLLAPSED_ROW_CLASS)) {
                headerRow = row.previousElementSibling;
            }

            row.setAttribute("inert", "");
            rows.push(row);

            if (headerRow && (!row.nextElementSibling?.classList.contains(COLLAPSED_ROW_CLASS))) {
                this.collapsibleRows.push(new CollapsibleRows(headerRow, rows));
                rows = [];
            }
        }
    }

    public disconnectedCallback(): void {
        this.collapsibleRows.forEach(row => row.cleanup());
    }
}

class CollapsibleRows {

    private toggle: (event: Event) => void;
    private handleKeydown: EventListener;

    public constructor(
        private headerRow: Element,
        private rows: Element[],
        private clickTracking: ClickTracking = resolve(ClickTracking)
    ) {
        headerRow.classList.add(COLLAPSED_ROW_HEADER_CLASS);
        headerRow.setAttribute("tabindex", "0");
        headerRow.setAttribute("role", "button");
        headerRow.setAttribute("aria-expanded", "false");

        this.toggle = (event: Event) => {
            this.clickTracking.handleButtonInteract(event, "collapsible-row-toggle");

            const isOpen = this.headerRow.classList.toggle("open");
            this.headerRow.setAttribute("aria-expanded", String(isOpen));
            this.rows.forEach(row => {
                row.classList.toggle(COLLAPSED_ROW_CLASS);
                row.toggleAttribute("inert");
            });
        };

        this.handleKeydown = (event: Event) => {
            const keyEvent = event as KeyboardEvent;
            handleActivationKey(keyEvent, () => this.toggle(event));
        };

        this.headerRow.addEventListener("click", this.toggle);
        this.headerRow.addEventListener("keydown", this.handleKeydown);
    }

    public cleanup(): void {
        this.headerRow.removeEventListener("click", this.toggle);
        this.headerRow.removeEventListener("keydown", this.handleKeydown);
    }
}

customElements.define("eop-table", EopTable);